import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { orcanosItemUrl } from '../api/orcanosClient';
import { psApi, fmtMoney, fmtQty, statusTone } from './psApi';
import PsExportModal from './PsExportModal';
import { IconBtn } from './PsTree';
import { KindIcon } from '../tree/icons';
import { PsBtnIcon } from './psIcons';
import { CompareResult } from './PsCompare';
import { MergeModal, NewBranchModal, PullModal, WhereUsedModal, BaselineModal, useConfirm } from './PsModals';

const tone = (h) => (h >= 80 ? 'good' : h >= 60 ? 'warn' : 'bad');
const when = (s) => String(s || '').slice(0, 16).replace('T', ' ');

function useLoad(fn, deps, onAuth) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const reload = useCallback(async () => {
    try { setData(await fn()); setError(''); } catch (e) { if (!onAuth(e)) setError(e.message); }
  }, deps); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setData(null); reload(); }, [reload]);
  return [data, error, reload];
}

// ─── Dashboard ────────────────────────────────────────────────────────────

export function PsDashboard({ go, onAuth, compact }) {
  const [data, error] = useLoad(() => psApi.dashboard(), [], onAuth);
  if (error) return <div className="ps-error" role="alert">{error}</div>;
  if (!data) return <div className="ps-muted">Loading…</div>;
  if (!data.cards.length) {
    return <div className="ps-empty"><p>No product structures yet.</p><p className="ps-muted">Open <b>Product structures</b> and add a product to start.</p></div>;
  }
  if (compact) {
    // One line per product (folded from the page head); a click opens its structure.
    return (
      <div className="ps-mini-list">
        {data.cards.map((c) => {
          const t = tone(c.health);
          return (
            <button key={c.root.node} type="button" className="ps-mini" onClick={() => go('ps-structures', { root: c.root.node })}
              title={`Open the structure of ${c.root.key}`}>
              <span className={`ps-mini-score ps-score--${t}`} title="Health: 100 = nothing to fix">{c.health}</span>
              <span className="ps-mini-name"><span className="ps-key">{c.root.key}</span> <b>{c.root.name}</b></span>
              <span className="ps-mini-n">
                <span><b>{c.parts}</b> parts</span>
                <span><b>{c.depth}</b> levels</span>
                <span><b>{fmtMoney(c.cost)}</b> cost</span>
                <span><b>{c.open_branches}</b> branches</span>
                <span className={c.issues.length ? 'ps-mini-bad' : undefined}><b>{c.issues.length}</b> issues</span>
              </span>
            </button>
          );
        })}
      </div>
    );
  }
  return (
    <div className="ps-cards">
      {data.cards.map((c) => {
        const t = tone(c.health);
        const node = c.root.node;
        return (
          <article key={node} className="ps-card">
            <div className="ps-card-top">
              <div>
                <div className="ps-key">{c.root.key}</div>
                <h2>{c.root.name}</h2>
                <p className="ps-muted ps-small">{c.last_baseline
                  ? `Last baseline ${c.last_baseline.version_label || ''} · ${c.last_baseline.name} · ${when(c.last_baseline.created_at)}`
                  : 'No baseline yet'}</p>
              </div>
              <div className={`ps-score ps-score--${t}`} title="Health: 100 = nothing to fix"><b>{c.health}</b><span>health</span></div>
            </div>
            <div className="ps-bar"><i className={`ps-score--${t}`} style={{ width: `${c.health}%` }} /></div>
            <div className="ps-stats">
              <div><b>{c.parts}</b><span>parts</span></div>
              <div><b>{c.depth}</b><span>levels</span></div>
              <div><b>{fmtMoney(c.cost)}</b><span>rolled-up cost</span></div>
              <div><b>{c.open_branches}</b><span>open branches</span></div>
            </div>
            <h3>Issues</h3>
            {c.issues.length === 0 && <p className="ps-muted ps-small">Nothing to fix.</p>}
            {c.issues.map((i, k) => (i.parts
              ? <IssueGroup key={k} issue={i} onLocate={(n) => go('ps-structures', { root: node, locate: n })} />
              : (
                <button key={k} type="button" className="ps-issue"
                  onClick={() => (i.node ? go('ps-structures', { root: node, locate: i.node })
                    : i.compare ? go('ps-baselines', { root: node, a: i.compare })
                      : go('ps-baselines', { root: node }))}>
                  <span className={`ps-sev ps-sev--${i.sev}`}>{i.sev}</span><span>{i.text}</span>
                </button>
              )))}
            <div className="ps-card-actions">
              <button type="button" className="ps-btn" onClick={() => go('ps-structures', { root: node })}>Open structure</button>
              <button type="button" className="ps-btn ps-btn--ghost" onClick={() => go('ps-baselines', { root: node })}><PsBtnIcon name="baselines" />Baselines</button>
              <button type="button" className="ps-btn ps-btn--ghost" onClick={() => go('ps-branches', { root: node })}><PsBtnIcon name="branches" />Branches</button>
            </div>
          </article>
        );
      })}
    </div>
  );
}

// Per-part issues of one kind arrive as ONE line (`parts`); it opens to list them.
function IssueGroup({ issue, onLocate }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="ps-issue-group">
      <button type="button" className="ps-issue" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <span className={`ps-sev ps-sev--${issue.sev}`}>{issue.sev}</span><span>{issue.text}</span>
        <span className="ps-issue-more">{open ? 'Hide ▴' : 'Show ▾'}</span>
      </button>
      {open && (
        <div className="ps-issue-parts">
          {issue.parts.map((p) => (
            <button key={p.node} type="button" className="ps-issue ps-issue--part" onClick={() => onLocate(p.node)}>
              <span>{p.text}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Part usage (Part Catalog + where each part is used) ─────────────────

export function PsParts({ go, onAuth }) {
  const [data, error] = useLoad(() => psApi.usage(), [], onAuth);
  const [query, setQuery] = useState('');
  const [wu, setWu] = useState(null);
  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (data?.items || []).filter((r) => !q || `${r.key} ${r.name}`.toLowerCase().includes(q));
  }, [data, query]);
  if (error) return <div className="ps-error" role="alert">{error}</div>;
  if (!data) return <div className="ps-muted">Loading…</div>;
  return (
    <>
      <div className="ps-toolbar">
        <input className="ps-search" type="search" placeholder="Search key or name" aria-label="Search parts" value={query} onChange={(e) => setQuery(e.target.value)} />
        <span className="ps-muted ps-small">{rows.length} part{rows.length === 1 ? '' : 's'} used in product structures</span>
      </div>
      <div className="ps-tw">
        <div className="ps-thead ps-g-parts"><span>Key</span><span>Name</span><span>Type</span><span>Rev</span><span>Status</span><span>Used in</span><span className="ps-num">Total qty</span><span /></div>
        {rows.map((r) => (
          <div key={r.node} className="ps-trow ps-g-parts">
            <span className="ps-cell-item"><KindIcon kind={r.type === 'DMS' ? 'doc' : 'part'} />
              {r.missing ? <span className="ps-ikey">{r.key}</span>
                : <a className="ps-ikey" href={orcanosItemUrl({ type: r.type, itemId: r.id })} target="_blank" rel="noopener noreferrer">{r.key}</a>}</span>
            <span>{r.name}{r.missing && <span className="ps-badge ps-badge--miss" style={{ marginLeft: 6 }}>Not in the filter</span>}</span>
            <span>{r.type}</span>
            <span>{r.rev}</span>
            <span className={'ps-st ps-st--' + statusTone(r.status)}>{r.status}</span>
            <span className="ps-small">{r.used_in.map((u, i) => (
              <React.Fragment key={u.root}>{i > 0 && ' · '}
                <button type="button" className="ps-btn ps-btn--link" style={{ padding: 0, minHeight: 0 }}
                  onClick={() => go('ps-structures', { root: u.root, locate: r.node })}>{u.key} ×{fmtQty(u.qty)}</button>
              </React.Fragment>))}</span>
            <span className="ps-num">{fmtQty(r.total)}</span>
            <span className="ps-acts"><button type="button" className="ps-btn ps-btn--link"
              onClick={async () => { try { setWu(await psApi.whereUsed(r.node)); } catch (e) { onAuth(e); } }}>Where used</button></span>
          </div>
        ))}
      </div>
      {wu && <WhereUsedModal data={wu} onClose={() => setWu(null)} onLocate={(root, n) => go('ps-structures', { root, locate: n })} />}
    </>
  );
}

// ─── Baselines + compare ──────────────────────────────────────────────────

// A pill that opens a list — the compare's From / To picker. Options with `disabled` show greyed.
function PillCombo({ value, options, onChange, disabled, label }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', esc); };
  }, [open]);
  const cur = options.find((o) => o.id === value);
  return (
    <div className="ps-pcombo" ref={ref}>
      <button type="button" className="ps-pill ps-pill--on ps-pcombo-btn" disabled={disabled} aria-label={label}
        aria-haspopup="listbox" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        {cur ? cur.label : '—'} <span className="ps-pcombo-caret">▾</span>
      </button>
      {open && (
        <ul className="ps-pcombo-menu" role="listbox" aria-label={label}>
          {options.map((o) => (
            <li key={o.id} role="option" aria-selected={o.id === value} aria-disabled={!!o.disabled}
              className={'ps-pcombo-opt' + (o.id === value ? ' is-on' : '') + (o.disabled ? ' is-off' : '')}
              title={o.disabled ? o.why : undefined}
              onClick={() => { if (!o.disabled) { onChange(o.id); setOpen(false); } }}>{o.label}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function PsBaselines({ root, rootRow, canEdit, initialA, initialB, showToast, onAuth }) {
  const [list, error, reload] = useLoad(() => psApi.baselines(root), [root], onAuth);
  const [branches] = useLoad(() => psApi.branches(root), [root], onAuth);
  const [a, setA] = useState(initialA || '');
  const [b, setB] = useState(initialB || 'current');
  const [cmp, setCmp] = useState(null);
  const [cmpErr, setCmpErr] = useState('');
  const [open, setOpen] = useState(null);
  const [taking, setTaking] = useState(false);
  const [exporting, setExporting] = useState(null);
  const [confirmEl, ask] = useConfirm();

  const bls = list?.baselines || [];
  const opts = [{ id: 'current', label: 'Current' }]
    .concat(bls.map((x) => ({ id: `b${x.id}`, label: `${x.version_label ? x.version_label + ' · ' : ''}${x.name}` })))
    .concat((branches?.branches || []).filter((x) => x.status === 'open').map((x) => ({ id: `br${x.id}`, label: `Branch: ${x.name}` })));
  const A = a && opts.some((o) => o.id === a) ? a : (bls.length ? `b${bls[bls.length - 1].id}` : '');
  // Age order: baselines oldest → newest (list is ascending), then Current and open branches (both "now").
  const rank = (id) => (id.startsWith('b') && !id.startsWith('br') ? bls.findIndex((x) => `b${x.id}` === id) : bls.length);
  const okPair = !!A && A !== b && rank(A) <= rank(b);
  const fromOpts = opts.map((o) => ({ ...o, disabled: o.id === b || rank(o.id) > rank(b), why: o.id === b ? 'Already the To side' : 'Newer than the To side' }));
  const toOpts = opts.map((o) => ({ ...o, disabled: o.id === A || rank(o.id) < rank(A), why: o.id === A ? 'Already the From side' : 'Older than the From side' }));

  useEffect(() => {
    if (!okPair) { setCmp(null); return; }
    let off = false;
    setCmpErr('');
    psApi.compare(root, A, b).then((d) => { if (!off) setCmp(d); })
      .catch((e) => { if (!off && !onAuth(e)) setCmpErr(e.message); });
    return () => { off = true; };
  }, [root, A, b, okPair, onAuth]);

  if (error) return <div className="ps-error" role="alert">{error}</div>;
  if (!list) return <div className="ps-muted">Loading…</div>;
  return (
    <>
      <div className="ps-toolbar">
        <span className="ps-muted ps-small">A frozen copy of the structure, with the revision each part had at that moment.</span>
        {canEdit && <button type="button" className="ps-btn" onClick={() => setTaking(true)}><PsBtnIcon name="takeBaseline" />Take baseline</button>}
      </div>
      {bls.length === 0 ? <div className="ps-empty"><p>No baselines yet for this product.</p></div> : (
        <div className="ps-tw">
          <div className="ps-thead ps-g-bl"><span>Name</span><span>Version</span><span>Taken (UTC)</span><span>By</span><span className="ps-num">Parts</span><span className="ps-num">Cost</span><span /></div>
          {bls.slice().reverse().map((x) => (
            <div key={x.id} className="ps-trow ps-g-bl">
              <span><b>{x.name}</b>{x.note && <span className="ps-muted ps-small"> · {x.note}</span>}</span>
              <span>{x.version_label}</span>
              <span>{when(x.created_at)}</span>
              <span>{x.created_by}</span>
              <span className="ps-num">{x.parts}</span>
              <span className="ps-num">{fmtMoney(x.cost)}</span>
              <span className="ps-acts">
                <IconBtn icon="open" label="Open" onClick={async () => { try { setOpen(await psApi.baseline(x.id)); } catch (e) { onAuth(e); } }} />
                <IconBtn icon="compare" label="Compare with current" onClick={() => { setA(`b${x.id}`); setB('current'); }} />
                <IconBtn icon="export" label="Export" onClick={() => setExporting(x.id)} />
                {canEdit && <IconBtn icon="remove" label="Delete" danger
                  onClick={async () => { if (await ask({ title: 'Delete baseline?', message: `Delete baseline ${x.name}? This can't be undone.` })) { await psApi.deleteBaseline(x.id).catch((e) => showToast(e.message, 'error')); reload(); } }} />}
              </span>
            </div>
          ))}
        </div>
      )}

      <h3 style={{ marginTop: 28 }}>Compare</h3>
      <div className="ps-row">
        <span className="ps-lbl">From</span>
        <PillCombo label="Compare from" value={A} options={fromOpts} onChange={setA} disabled={bls.length === 0} />
        <span className="ps-lbl" style={{ marginLeft: 12 }}>To</span>
        <PillCombo label="Compare to" value={b} options={toOpts} onChange={setB} disabled={bls.length === 0} />
      </div>
      {cmpErr && <div className="ps-error">{cmpErr}</div>}
      {bls.length === 0
        ? <p className="ps-muted" style={{ marginTop: 10 }}>Take a baseline to compare.</p>
        : !okPair && <p className="ps-muted" style={{ marginTop: 10 }}>Pick two different points, the older one in From.</p>}
      {cmp && okPair && <CompareResult cmp={cmp} />}

      {exporting && <PsExportModal root={root} baseline={exporting} onClose={() => setExporting(null)} onAuth={onAuth} />}
      {taking && (
        <BaselineModal rootLabel={`${rootRow.key} ${rootRow.name}`} onClose={() => setTaking(false)}
          onSave={async (x) => {
            const out = await psApi.takeBaseline({ root, ...x });
            setTaking(false);
            showToast(`Baseline taken — ${out.parts} parts, ${fmtMoney(out.cost)}.`);
            setA(`b${out.id}`);
            reload();
          }} />
      )}
      {confirmEl}
      {open && (
        <div className="ps-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) setOpen(null); }}>
          <div className="ps-modal ps-modal--wide" role="dialog" aria-modal="true" aria-label="Baseline">
            <div className="ps-mhead"><h2>{open.version_label} {open.name}</h2>
              <button type="button" className="ps-x" aria-label="Close" onClick={() => setOpen(null)}>✕</button></div>
            <div className="ps-mbody">
              <p className="ps-muted ps-small">Taken {when(open.created_at)} UTC by {open.created_by} · {open.parts} parts · {fmtMoney(open.cost)}</p>
              <div className="ps-tw">
                <div className="ps-thead ps-g-base"><span>Item</span><span>Rev</span><span>Status</span><span>Qty</span><span>Find</span><span>Ref. des.</span></div>
                {open.rows.map((r, i) => (
                  <div key={i} className={'ps-trow ps-g-base' + (r.depth === 0 ? ' ps-trow--root' : '')}>
                    <span style={{ paddingLeft: r.depth * 20 }}><span className="ps-ikey">{r.key}</span> {r.name}</span>
                    <span>{r.rev}</span><span className={'ps-st ps-st--' + statusTone(r.status)}>{r.status}</span>
                    <span>{r.depth ? fmtQty(r.qty) : ''}</span><span>{r.find_no}</span><span className="ps-small">{r.ref_des}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ─── Branches ─────────────────────────────────────────────────────────────

export function PsBranches({ root, rootRow, go, showToast, onAuth }) {
  const [data, error, reload] = useLoad(() => psApi.branches(root), [root], onAuth);
  const [sel, setSel] = useState(null);
  const [modal, setModal] = useState(null);
  const [confirmEl, ask] = useConfirm();
  const list = data?.branches || [];
  const br = list.find((b) => b.id === sel) || list.find((b) => b.status === 'open') || list[0];
  const canEdit = !!data?.can_edit;
  const rootLabel = rootRow ? rootRow.name || rootRow.key : '';

  if (error) return <div className="ps-error" role="alert">{error}</div>;
  if (!data) return <div className="ps-muted">Loading…</div>;
  const kindLabel = (b) => (b.status === 'merged' ? 'Merged' : b.status === 'closed' ? 'Closed' : b.kind === 'variant' ? 'Variant' : 'Change proposal');
  const kindCls = (b) => (b.status === 'open' ? b.kind : b.status);
  const act = async (fn, msg) => {
    try { await fn(); if (msg) showToast(msg); await reload(); } catch (e) { if (!onAuth(e)) showToast(e.message, 'error'); }
  };

  return (
    <>
      <div className="ps-toolbar">
        <span className="ps-muted ps-small">A copy of the structure you change on its own: a long-lived <b>variant</b> or a <b>change proposal</b> to merge back.</span>
        {canEdit && <button type="button" className="ps-btn" onClick={() => setModal({ kind: 'new' })}>+ New branch</button>}
      </div>
      <div className="ps-twocol">
        <div className="ps-brlist">
          {list.length === 0 && <p className="ps-muted">No branches of this product.</p>}
          {list.map((b) => (
            <button key={b.id} type="button" className={'ps-brcard' + (br && br.id === b.id ? ' ps-brcard--on' : '')} onClick={() => setSel(b.id)}>
              <span className={`ps-kind ps-kind--${kindCls(b)}`}>{kindLabel(b)}</span>
              <b>{b.name}</b>
              <span className="ps-muted ps-small">{b.changes.length} change{b.changes.length === 1 ? '' : 's'} · {b.created_by} · {when(b.created_at)}
                {b.parent_id && list.find((x) => x.id === b.parent_id) ? ` · from ${list.find((x) => x.id === b.parent_id).name}` : ''}</span>
            </button>
          ))}
        </div>
        <div className="ps-brdetail">
          {br && (
            <div className="ps-card">
              <div className="ps-card-top">
                <div>
                  <span className={`ps-kind ps-kind--${kindCls(br)}`}>{kindLabel(br)}</span>
                  <h2 style={{ marginTop: 6 }}>{br.name}</h2>
                  <p className="ps-muted ps-small">Branched from {br.parent_id && list.find((x) => x.id === br.parent_id) ? `branch “${list.find((x) => x.id === br.parent_id).name}”` : 'main'} by {br.created_by} on {when(br.created_at)} UTC</p>
                </div>
                {br.status === 'open' && (
                  <div className="ps-row">
                    <button type="button" className="ps-btn ps-btn--ghost ps-btn--sm" onClick={() => go('ps-structures', { root, branch: String(br.id) })}>Open in tree</button>
                    <button type="button" className="ps-btn ps-btn--ghost ps-btn--sm" onClick={() => go('ps-baselines', { root, a: 'current', b: `br${br.id}` })}>Compare</button>
                    <button type="button" className="ps-btn ps-btn--ghost ps-btn--sm"
                      onClick={async () => { try { setModal({ kind: 'pull', data: await psApi.pull(br.id) }); } catch (e) { onAuth(e); } }}>Pull from main</button>
                    {canEdit && <button type="button" className="ps-btn ps-btn--sm" disabled={!br.changes.length} onClick={() => setModal({ kind: 'merge' })}>Merge to main…</button>}
                  </div>
                )}
              </div>
              {br.status === 'open' && br.kind === 'variant' && (
                <div className="ps-banner ps-banner--note" style={{ marginTop: 12 }}>A variant usually stays open. Merging moves its changes into main; the variant stays open with nothing left to merge.</div>
              )}
              <h3>Changes against main</h3>
              {br.changes.length === 0 ? <p className="ps-muted">No changes — this branch matches main.</p> : (
                <div className="ps-tw">
                  <div className="ps-thead ps-g-chg"><span>Change</span><span>Under</span><span>Shared?</span><span /></div>
                  {br.changes.map((c) => (
                    <div key={c.index} className="ps-trow ps-g-chg">
                      <span>{c.text}{c.conflict && <span className="ps-st ps-st--bad"> · no longer in main</span>}</span>
                      <span className="ps-ikey">{c.under}</span>
                      <span className="ps-small">{c.shared ? `Yes — ${c.used_in.map((u) => u.key).join(', ')}` : 'No'}</span>
                      <span className="ps-acts">{canEdit && br.status === 'open' && (
                        <button type="button" className="ps-btn ps-btn--link" onClick={async () => { if (await ask({ title: 'Drop this change?', message: `Drop “${c.text}” from ${br.name}?`, confirmLabel: 'Drop' })) act(() => psApi.dropChange(br.id, c.index), 'Change dropped.'); }}>Drop</button>
                      )}</span>
                    </div>
                  ))}
                </div>
              )}
              {canEdit && (
                <div className="ps-card-actions">
                  {br.status === 'open' && <button type="button" className="ps-btn ps-btn--ghost ps-btn--sm"
                    onClick={() => { if (window.confirm(`Close ${br.name}? Its changes are kept but it can't be edited or merged.`)) act(() => psApi.closeBranch(br.id), 'Branch closed.'); }}>Close branch</button>}
                  <button type="button" className="ps-btn ps-btn--danger ps-btn--sm"
                    onClick={async () => {
                      if (await ask({ title: `Delete branch ${br.name}?`, message: `The branch and all ${br.changes.length} of its changes are deleted. Main is not changed. This can't be undone.`,
                        confirmLabel: 'Delete branch', typeWord: 'DELETE' })) act(() => psApi.deleteBranch(br.id), 'Branch deleted.');
                    }}>Delete</button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {modal?.kind === 'new' && (
        <NewBranchModal rootLabel={rootLabel} onClose={() => setModal(null)}
          onCreate={async (x) => {
            const out = await psApi.addBranch({ root, ...x });
            setModal(null);
            setSel(out.id);
            showToast('Branch created. Open it in the tree to make changes there.');
            reload();
          }} />
      )}
      {modal?.kind === 'merge' && br && (
        <MergeModal branch={br} rootLabel={rootLabel} onClose={() => setModal(null)}
          onMerge={async (scopes) => {
            const out = await psApi.merge(br.id, scopes);
            setModal(null);
            showToast(`Merged ${out.merged} change${out.merged === 1 ? '' : 's'} into main`
              + (out.own_version ? ` — ${out.own_version} as ${rootLabel}'s own version` : '')
              + (out.skipped.length ? `; ${out.skipped.length} left in the branch` : '') + '.');
            reload();
          }} />
      )}
      {modal?.kind === 'pull' && br && (
        <PullModal data={modal.data} onClose={() => setModal(null)}
          onAccept={() => act(async () => { await psApi.pull(br.id, true); setModal(null); }, 'Marked as seen.')} />
      )}
      {confirmEl}
    </>
  );
}
